import React from 'react';
import { RideData } from '../ReportsComponents/circleChart';
import { RideStatus, Status } from './types';
import { formatMonthLabel } from '@component/LightVersion/Reports/yAxisHelpers';
import { assetColorByIndex } from '@component/LightVersion/Reports/constants';
import ChartTableDynamicSkeleton from '@src/uikit/Loader/DashboardSkeleton/ChartTableDynamicSkeleton';
import { blurStyle } from '@component/LightVersion/Reports/helpers';

export interface RoutineData {
  label: string;
  month: string;
  routineCount: number;
  lifeSustainingCount: number;
}

export interface CountryRideData {
  label: string;
  country: string;
  rideCount: number;
}

export interface IProps {
  data: RideData[] | RoutineData[] | CountryRideData[] | any;
  colors?: Record<RideStatus, string>;
  statuses?: Status[];
  hasArrowIncDec?: boolean;
  hasColorKey?: boolean;
  dataTestId?: string;
  loading?: boolean;
  isFetching?: boolean;
}

const ChartTableDynamic = ({
  data,
  colors,
  statuses,
  hasArrowIncDec = true,
  hasColorKey = true,
  dataTestId,
  loading = false,
  isFetching = false,
}: IProps): JSX.Element | null => {
  if (loading) {
    return (
      <div className="ride-status-table-wrapper">
        <ChartTableDynamicSkeleton />
      </div>
    );
  }
  if (!data || data.length === 0) return null;
  return (
    <div
      data-test-id={dataTestId}
      className="ride-status-table-wrapper"
      style={{ ...blurStyle(isFetching) }}
    >
      <table
        data-test-id={`${dataTestId}-table`}
        className="ride-status-table one-line-titles"
      >
        <thead>
          <tr>
            <th></th>
            {data.map((item) => (
              <th data-test-id={`${dataTestId}-${item.month}`} key={item.month}>
                {formatMonthLabel(item.month)}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {statuses?.map((status, index) => (
            <tr key={status.key} data-test-id={`${dataTestId}-${status.label}`}>
              <td>
                <div className="header-title">
                  {status.label}
                  {!hasColorKey && (
                    <span
                      className="ride-chart-legend-color"
                      style={{ background: assetColorByIndex(index) }}
                    />
                  )}
                  {hasColorKey &&
                    status.label !== 'Total' &&
                    status.colorKey &&
                    colors?.[status.colorKey] && (
                      <span
                        className="ride-chart-legend-color"
                        style={{ background: colors[status.colorKey] }}
                      />
                    )}
                </div>
              </td>

              {data.map((item) => {
                const value = item[status.key as keyof RideData] as {
                  count: number;
                  percentage: number;
                  increase: boolean;
                };

                if (!value)
                  return (
                    <td
                      data-test-id={`${dataTestId}-${item.month}`}
                      key={item.month}
                    >
                      –
                    </td>
                  );

                const arrow =
                  value.percentage === 0 ? '→' : value.increase ? '↑' : '↓';

                return (
                  <td
                    data-test-id={`${dataTestId}-${item.month}`}
                    key={item.month}
                  >
                    <div className="cell-data">
                      <div className="count">{value.count}</div>
                      {hasArrowIncDec && status.label !== 'Total' && (
                        <div className="percentage">
                          <span className="arrow">{arrow}</span>
                          <span className="percent">
                            {Number(value.percentage) === 0
                              ? '0.00%'
                              : `${value.percentage}%`}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ChartTableDynamic;
